import { useState, useCallback, createContext } from "react";
import storage from "../hooks/storage";

/**
 * OAuth Context - Manages OAuth state for existing customer migration
 *
 * This context handles:
 * 1. Checking if OAuth tokens exist for a subdomain
 * 2. Storing OAuth authorization status
 * 3. Managing the OAuth flow state
 */

const OAuthClientContext = createContext({
  isOAuthChecked: false,
  hasTokens: false,
  isCheckingOAuth: true,
  subdomain: "",
  isAdmin: false,
  oauthError: null,
  setOAuthComplete: () => {},
  clearOAuthState: () => {},
  setOAuthStatusFromAuth: (hasToken, subdomainValue, isAdminValue) => {},
});

export const OAuthClientContextProvider = (props) => {
  // Initialize from localStorage to persist across page reloads
  const cachedOAuthStatus = storage.get('oauth_status');
  const cachedUserInfo = storage.get('oauth_user_info');
  const initialHasTokens = cachedOAuthStatus === 'authorized';

  const [isOAuthChecked, setIsOAuthChecked] = useState(initialHasTokens);
  const [hasTokens, setHasTokens] = useState(initialHasTokens);
  const [isCheckingOAuth, setIsCheckingOAuth] = useState(!initialHasTokens);
  const [subdomain, setSubdomain] = useState(cachedUserInfo?.subdomain || "");
  const [isAdmin, setIsAdmin] = useState(cachedUserInfo?.isAdmin || false);
  const [oauthError, setOauthError] = useState(null);

  /**
   * Mark OAuth as complete after successful authorization
   */
  const setOAuthComplete = useCallback(() => {
    setHasTokens(true);
    if (subdomain) {
      storage.set(`oauth_status`, "authorized");
    }
  }, [subdomain]);

  /**
   * Clear OAuth state (for logout or re-authorization)
   */
  const clearOAuthState = useCallback(() => {
    setHasTokens(false);
    setIsOAuthChecked(false);
    setIsCheckingOAuth(true);
    setOauthError(null);
    setSubdomain("");
    setIsAdmin(false);
    storage.remove(`oauth_status`);
    storage.remove(`oauth_user_info`);
  }, []);

  /**
   * Set OAuth status directly from auth API response
   * This avoids calling checkOAuthStatus API when we already know the status
   */
  const setOAuthStatusFromAuth = useCallback((hasToken, subdomainValue, isAdminValue) => {
    setSubdomain(subdomainValue);
    setHasTokens(hasToken);
    setIsAdmin(isAdminValue || false);
    setIsOAuthChecked(true);
    setIsCheckingOAuth(false);

  

    if (hasToken && subdomainValue) {
      storage.set(`oauth_status`, "authorized");
    }
  }, []);

  const contextValue = {
    isOAuthChecked,
    hasTokens,
    isCheckingOAuth,
    subdomain,
    isAdmin,
    oauthError,
    setOAuthComplete,
    clearOAuthState,
    setOAuthStatusFromAuth,
  };

  return (
    <OAuthClientContext.Provider value={contextValue}>
      {props.children}
    </OAuthClientContext.Provider>
  );
};

export default OAuthClientContext;